import React, { useCallback, useState } from "react";
import { Plus, Pencil, History } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import CatalogTable from "./CatalogTable";
import { CatalogLoadingCard, CatalogErrorCard } from "./CatalogStateCard";
import EntityFormDialog from "./EntityFormDialog";
import StatusBadge from "./StatusBadge";
import StatusSelectField from "./StatusSelectField";
import { useEmailCatalogEntityList } from "../../../hooks/useEmailCatalogEntityList";

/**
 * AssetFamilyPanel — EMAILS-IMPL-009
 *
 * A single, configuration-driven panel reused by the SIX governed Asset
 * families that share an identical shape -- one name, one governed status,
 * one canonical content field, and nothing else family-specific: Subject,
 * Preview, Hook, Email Body, Offer, P.S. (SUB/PRV/HOK/EML/OFF/PS). CTA
 * (fingerprint fields), Landing Page (internal reference instead of
 * content), and Image (file upload) each differ enough in shape that they
 * get their own dedicated panel component instead -- mirroring the
 * backend's own generic-dispatch-for-alike-families /
 * dedicated-code-for-Image split (email_marketing_asset_authoring_service.
 * py's own module docstring makes the identical argument). Every field the
 * `config` prop supplies is named explicitly by the caller (Subjects
 * Panel.jsx etc.) -- nothing here is inferred or reflected dynamically, so
 * this stays a small shared shell, not an opaque framework.
 *
 * No `list()` call exists: no `emails`-gated read endpoint exists for any
 * Asset family yet (see lib/emailMarketingApi.js's own note). `items`
 * starts empty and is populated only by this session's own create/edit/
 * new-Version responses -- a disclosed limitation, not a silent one; see
 * the EMAILS-IMPL-009 completion report's Open Issues.
 *
 * IMMUTABLE VERSION UX (checkpoint §6/§7): editing canonical content is
 * NEVER offered through the "Edit metadata" dialog (name/status only,
 * mirroring EMAILS-IMPL-007's Program/Campaign/Angle/Audience Definition
 * edit dialogs exactly -- there is no content field in that form at all).
 * A content change is only ever reachable through the separate "New
 * Version" action below, whose dialog explicitly shows the CURRENT version
 * as read-only context, requires an operator-typed NEW version string (no
 * SemVer auto-increment -- the backend deliberately requires this, see
 * email_marketing_asset_authoring_service.py's own module docstring), and
 * is labeled "Create New Version", never "Save Changes".
 */
export default function AssetFamilyPanel({ config }) {
  const {
    familyCode,
    testIdPrefix,
    idField,
    contentField,
    contentLabel,
    contentInputType,
    contentPlaceholder,
    labelSingular,
    labelPlural,
    icon: Icon,
    statusValues,
    createDescription,
    api,
  } = config;

  // The route-segment-shaped prefix (e.g. "subjects", "email-bodies") --
  // matches the plural URL segments used across the rest of this module
  // (CatalogTable/StatusBadge/etc test ids for Programs/Campaigns/etc all
  // follow this same convention) and the bespoke Ctas/LandingPages/Images
  // panels' own test ids, rather than the shorter 3-letter familyCode.
  const testPrefix = testIdPrefix;

  // Stable identities across renders -- useEmailCatalogEntityList's
  // `refresh`/`createItem`/`updateItem`/`createVersionItem` are each a
  // useCallback keyed to these; passing a fresh inline arrow function
  // literal here every render (the first version of this component did)
  // gives `refresh` a new identity every render, which retriggers its own
  // effect every render -> an infinite render loop (caught by Jest's
  // "Maximum update depth exceeded" during SubjectsPanel.test.jsx). `api`
  // and `idField` both come from the module-level `config` object each
  // caller (SubjectsPanel.jsx etc.) defines outside its component
  // function, so they are themselves already stable.
  //
  // EMAILS-IMPL-009A: `list` now calls the real, persistent
  // `emails`-gated backend catalog (`api.list`, e.g. getSubjects()) --
  // replacing EMAILS-IMPL-009's `async () => []` session-only stub. The
  // server is authoritative: on mount, every Asset this tenant has ever
  // created reappears, not just ones created in the current browser
  // session. create/edit/new-Version still upsert the mutation response
  // locally (no extra refetch) -- the response already reflects the
  // server's own persisted state, so this is equivalent to a refetch, not
  // a session-cache shortcut.
  const listFn = useCallback(() => api.list(), [api]);
  const createFn = useCallback(({ name, status, content }) => api.create({ name, status, content }), [api]);
  const updateFn = useCallback((id, { name, status }) => api.updateMetadata(id, { name, status }), [api]);
  const createVersionFn = useCallback((id, { version, content }) => api.createVersion(id, { version, content }), [api]);
  const getIdFn = useCallback((row) => row[idField], [idField]);

  const { items, loading, error, mutating, mutationError, clearMutationError, createItem, updateItem, createVersionItem } =
    useEmailCatalogEntityList({
      list: listFn,
      create: createFn,
      update: updateFn,
      createVersion: createVersionFn,
      getId: getIdFn,
    });

  const [expandedId, setExpandedId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", status: statusValues[0], content: "" });
  const [editingRow, setEditingRow] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", status: statusValues[0] });
  const [versioningRow, setVersioningRow] = useState(null);
  const [versionForm, setVersionForm] = useState({ version: "", content: "" });

  const openCreate = () => {
    setCreateForm({ name: "", status: statusValues[0], content: "" });
    clearMutationError();
    setCreateOpen(true);
  };

  const submitCreate = async () => {
    try {
      await createItem({ name: createForm.name.trim(), status: createForm.status, content: createForm.content });
      setCreateOpen(false);
    } catch {
      // mutationError already surfaced by the hook; keep the dialog open.
    }
  };

  const openEdit = (row) => {
    setEditingRow(row);
    setEditForm({ name: row.name, status: row.status });
    clearMutationError();
  };

  const submitEdit = async () => {
    try {
      await updateItem(editingRow[idField], {
        name: editForm.name.trim() !== editingRow.name ? editForm.name.trim() : undefined,
        status: editForm.status !== editingRow.status ? editForm.status : undefined,
      });
      setEditingRow(null);
    } catch {
      // dialog stays open; error shown inline
    }
  };

  const openVersion = (row) => {
    setVersioningRow(row);
    setVersionForm({ version: "", content: row[contentField] || "" });
    clearMutationError();
  };

  const submitVersion = async () => {
    try {
      await createVersionItem(versioningRow[idField], { version: versionForm.version.trim(), content: versionForm.content });
      setVersioningRow(null);
    } catch {
      // dialog stays open; error shown inline (e.g. 409 version conflict)
    }
  };

  if (loading) return <CatalogLoadingCard testId={`${testPrefix}-loading`} />;
  if (error) return <CatalogErrorCard message={error} testId={`${testPrefix}-error`} />;

  const ContentInput = contentInputType === "textarea" ? Textarea : Input;

  return (
    <div className="space-y-4" data-testid={`${testPrefix}-panel`}>
      <CatalogTable
        testId={`${testPrefix}-table`}
        title={labelPlural}
        icon={Icon}
        headerAction={
          <Button
            type="button"
            size="sm"
            className="gap-1.5 bg-violet-600 text-white hover:bg-violet-500"
            onClick={openCreate}
            data-testid={`new-${testPrefix}-button`}
          >
            <Plus className="h-4 w-4" /> New {labelSingular}
          </Button>
        }
        rows={items}
        getRowKey={(row) => row[idField]}
        onRowClick={(row) => setExpandedId(expandedId === row[idField] ? null : row[idField])}
        emptyTitle={`No ${labelPlural} yet`}
        emptySubtitle={`Create one with "New ${labelSingular}" to get started.`}
        columns={[
          {
            key: "name",
            label: labelSingular,
            render: (row) => (
              <div>
                <div className="text-sm font-medium text-slate-100">{row.name}</div>
                <div className="text-xs text-slate-400">{row[idField]}</div>
              </div>
            ),
          },
          { key: "status", label: "Status", render: (row) => <StatusBadge status={row.status} testId={`${testPrefix}-status-${row[idField]}`} /> },
          { key: "current_version", label: "Current Version", render: (row) => <span className="font-mono text-sm text-slate-300">{row.current_version || "—"}</span> },
        ]}
      />

      {items
        .filter((row) => row[idField] === expandedId)
        .map((row) => (
          <div key={row[idField]} className="rounded-lg border border-white/10 bg-white/5 p-4" data-testid={`${testPrefix}-detail-${row[idField]}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="grid flex-1 grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-4">
                <Detail label="Governed ID" value={row[idField]} mono />
                <Detail label="Status" value={row.status || "—"} />
                <Detail label="Current Version" value={row.current_version || "—"} mono />
                <Detail label="Updated" value={row.updated_at ? new Date(row.updated_at).toLocaleString() : "—"} />
                <div className="col-span-2 sm:col-span-4">
                  <div className="text-xs uppercase tracking-wide text-slate-500">{contentLabel} (current version)</div>
                  <div className="mt-1 whitespace-pre-wrap rounded-md border border-white/10 bg-black/20 p-2 text-sm text-slate-200">
                    {row[contentField] || "—"}
                  </div>
                </div>
              </div>
              <div className="flex shrink-0 flex-col gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="gap-1.5 border-white/10 text-slate-200"
                  onClick={() => openEdit(row)}
                  data-testid={`edit-${testPrefix}-${row[idField]}`}
                >
                  <Pencil className="h-3.5 w-3.5" /> Edit Metadata
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="gap-1.5 border-violet-400/30 text-violet-200"
                  onClick={() => openVersion(row)}
                  data-testid={`new-version-${testPrefix}-${row[idField]}`}
                >
                  <History className="h-3.5 w-3.5" /> Create New Version
                </Button>
              </div>
            </div>
          </div>
        ))}

      <EntityFormDialog
        testId={`create-${testPrefix}-dialog`}
        open={createOpen}
        onOpenChange={setCreateOpen}
        title={`New ${labelSingular}`}
        description={createDescription}
        onSubmit={submitCreate}
        submitting={mutating}
        error={mutationError}
        submitLabel={`Create ${labelSingular}`}
      >
        <div className="space-y-2">
          <Label htmlFor={`${testPrefix}-name`}>Name</Label>
          <Input
            id={`${testPrefix}-name`}
            value={createForm.name}
            onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
            required
            data-testid={`${testPrefix}-name-input`}
          />
        </div>
        <StatusSelectField
          id={`${testPrefix}-status`}
          value={createForm.status}
          onChange={(status) => setCreateForm((f) => ({ ...f, status }))}
          values={statusValues}
          testId={`${testPrefix}-status-select`}
        />
        <div className="space-y-2">
          <Label htmlFor={`${testPrefix}-content`}>{contentLabel}</Label>
          <ContentInput
            id={`${testPrefix}-content`}
            value={createForm.content}
            onChange={(e) => setCreateForm((f) => ({ ...f, content: e.target.value }))}
            placeholder={contentPlaceholder}
            required
            data-testid={`${testPrefix}-content-input`}
          />
        </div>
      </EntityFormDialog>

      {editingRow && (
        <EntityFormDialog
          testId={`edit-${testPrefix}-dialog`}
          open={!!editingRow}
          onOpenChange={(open) => !open && setEditingRow(null)}
          title={`Edit ${editingRow[idField]}`}
          description={`Rename or change the governed status of this ${labelSingular}. Canonical content cannot be changed here -- use "Create New Version" for that.`}
          onSubmit={submitEdit}
          submitting={mutating}
          error={mutationError}
          submitLabel="Save Changes"
        >
          <div className="space-y-2">
            <Label htmlFor={`${testPrefix}-edit-name`}>Name</Label>
            <Input
              id={`${testPrefix}-edit-name`}
              value={editForm.name}
              onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
              required
              data-testid={`${testPrefix}-edit-name-input`}
            />
          </div>
          <StatusSelectField
            id={`${testPrefix}-edit-status`}
            value={editForm.status}
            onChange={(status) => setEditForm((f) => ({ ...f, status }))}
            values={statusValues}
            testId={`${testPrefix}-edit-status-select`}
          />
        </EntityFormDialog>
      )}

      {versioningRow && (
        <EntityFormDialog
          testId={`new-version-${testPrefix}-dialog`}
          open={!!versioningRow}
          onOpenChange={(open) => !open && setVersioningRow(null)}
          title={`New Version — ${versioningRow[idField]}`}
          description="Creates a new, immutable Version. The current Version stays historically accessible and unchanged."
          onSubmit={submitVersion}
          submitting={mutating}
          error={mutationError}
          submitLabel="Create New Version"
        >
          <div className="rounded-md border border-white/10 bg-black/20 p-3 text-sm">
            <div className="flex justify-between text-slate-400">
              <span>Asset</span>
              <span className="font-mono text-slate-200" data-testid={`new-version-${testPrefix}-asset-id`}>{versioningRow[idField]}</span>
            </div>
            <div className="mt-1 flex justify-between text-slate-400">
              <span>Current version</span>
              <span className="font-mono text-slate-200" data-testid={`new-version-${testPrefix}-current`}>{versioningRow.current_version || "—"}</span>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${testPrefix}-new-version`}>New Version</Label>
            <Input
              id={`${testPrefix}-new-version`}
              value={versionForm.version}
              onChange={(e) => setVersionForm((f) => ({ ...f, version: e.target.value }))}
              placeholder="e.g. 2.0.0"
              required
              data-testid={`${testPrefix}-new-version-input`}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${testPrefix}-new-content`}>New {contentLabel}</Label>
            <ContentInput
              id={`${testPrefix}-new-content`}
              value={versionForm.content}
              onChange={(e) => setVersionForm((f) => ({ ...f, content: e.target.value }))}
              placeholder={contentPlaceholder}
              required
              data-testid={`${testPrefix}-new-content-input`}
            />
          </div>
        </EntityFormDialog>
      )}
    </div>
  );
}

function Detail({ label, value, mono }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className={mono ? "font-mono text-slate-200" : "text-slate-200"}>{value}</div>
    </div>
  );
}
